// src/utils/reportPdfUtils.js
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const A4 = { width: 210, height: 297 };
const MARGINS = { top: 14, right: 12, bottom: 16, left: 12 };
const PRINT_W = A4.width - MARGINS.left - MARGINS.right;   // 186mm

const slugify = (name) =>
  String(name || 'candidate')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9-_]/g, '')
    .toLowerCase() || 'candidate';

const waitForImages = (node) =>
  Promise.all(
    Array.from(node.querySelectorAll('img')).map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete && img.naturalWidth > 0) return resolve();
          img.onload = () => resolve();
          img.onerror = () => resolve();
        })
    )
  );

export async function downloadNodeAsPdf(node, studentName = 'candidate') {
  if (!node) throw new Error('No report node to export.');

  node.classList.add('pdf-capture-mode');

  try {
    await waitForImages(node);
    if (document.fonts?.ready) await document.fonts.ready;
    await new Promise((r) => requestAnimationFrame(() => r()));

    const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
    
    // Find the explicit page wrappers
    const pageNodes = node.querySelectorAll('.pdf-page');
    const pxPerMm = 3.7795275591; // Standard CSS pixel to mm conversion (96dpi)

    if (pageNodes.length > 0) {
      for (let i = 0; i < pageNodes.length; i++) {
        const pageNode = pageNodes[i];
        
        const canvas = await html2canvas(pageNode, {
          scale: 2,
          useCORS: true,
          letterRendering: true,
          backgroundColor: '#ffffff',
          scrollY: -window.scrollY,
          windowWidth: pageNode.scrollWidth,
          windowHeight: pageNode.scrollHeight,
        });

        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        
        // Calculate height in mm based on aspect ratio to prevent stretching
        const renderH = (canvas.height * PRINT_W) / canvas.width;

        if (i > 0) pdf.addPage();
        
        pdf.addImage(
          imgData,
          'JPEG',
          MARGINS.left,
          MARGINS.top,
          PRINT_W,
          renderH,
          undefined,
          'FAST'
        );
      }
    } else {
      // Fallback for legacy report
      const canvas = await html2canvas(node, { scale: 2, useCORS: true });
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const renderH = (canvas.height * PRINT_W) / canvas.width;
      pdf.addImage(imgData, 'JPEG', MARGINS.left, MARGINS.top, PRINT_W, renderH);
    }

    pdf.save(`${slugify(studentName)}-report.pdf`);
  } finally {
    node.classList.remove('pdf-capture-mode');
  }
}

export function printNode(_node) {
  window.print();
}